import { AxiosInstance } from 'axios';
import { HttpResponse } from '@/interfaces';
import axiosClient from './axiosClientConfig';
import {
	ConversationInfo,
	ConversationList,
	GetConversationPayload,
	InboxCaseData,
	InboxList,
	LawyerCases,
	LawyerClients,
	LawyerInboxList,
	NewAddMessagePayload,
	NewConversationPayload,
	SendMessageToAllPayload,
	SubjectsData,
	UnreadConversationsCounter,
} from '@/interfaces/inbox';

class InboxService {
	constructor(private client: AxiosInstance) {}

	async getInboxList({
		page,
		limit,
		search,
		caseId,
		status,
		clientId,
		subjectId,
		cases,
		aiEnabled,
	}: {
		page: number;
		limit: number;
		search?: string;
		caseId?: string;
		status?: string;
		clientId?: string;
		subjectId?: string;
		cases?: string;
		aiEnabled?: boolean;
	}): Promise<HttpResponse<InboxList>> {
		const params = new URLSearchParams({
			page: String(page),
			limit: String(limit),
			...(search && { search }),
			...(caseId && { caseId }),
			...(status && { status }),
			...(aiEnabled && { aiEnabled: String(aiEnabled) }),
		});

		if (clientId) {
			clientId.split(',').forEach((id) => params.append('clientId', id));
		}

		if (subjectId) {
			subjectId.split(',').forEach((id) => params.append('subjectId', id));
		}

		if (cases) {
			cases.split(',').forEach((id) => params.append('caseId', id));
		}

		return this.client.get('/inbox', { params });
	}

	async getLawyerInboxList({
		page,
		limit,
		search,
		caseId,
		status,
		clientId,
		subjectId,
		cases,
		startDate,
		endDate,
		aIEnabled,
	}: {
		page: number;
		limit: number;
		search?: string;
		caseId?: string;
		status?: string;
		clientId?: string;
		subjectId?: string;
		cases?: string;
		startDate?: string;
		endDate?: string;
		aIEnabled?: boolean;
	}): Promise<HttpResponse<LawyerInboxList>> {
		const params = new URLSearchParams({
			page: String(page),
			limit: String(limit),
			...(search && { search }),
			...(caseId && { caseId }),
			...(status && { status }),
			...(startDate && { start_date: startDate }),
			...(endDate && { end_date: endDate }),
			...(aIEnabled && { aiEnabled: String(aIEnabled) }),
		});

		if (clientId) {
			clientId.split(',').forEach((id) => params.append('clientId', id));
		}

		if (subjectId) {
			subjectId.split(',').forEach((id) => params.append('subjectId', id));
		}

		if (cases) {
			cases.split(',').forEach((id) => params.append('caseId', id));
		}
		console.log({ params: params });
		return this.client.get('/inbox/lawyer-messages', { params });
	}

	async getInboxSubjects(): Promise<HttpResponse<SubjectsData>> {
		return this.client.get('/inbox/subjects');
	}
	async getInboxUserCases(): Promise<HttpResponse<InboxCaseData>> {
		return this.client.get('/inbox/user-cases');
	}
	async postNewConversation(
		data: NewConversationPayload
	): Promise<HttpResponse<InboxCaseData>> {
		return this.client.post('/inbox', data);
	}
	async getUnreadConversationsCounter(): Promise<
		HttpResponse<UnreadConversationsCounter>
	> {
		return this.client.get('/inbox/unread-conversation-counter');
	}
	async getConversations({
		caseId,
		clientId,
		conversationId,
		page,
		limit,
	}: GetConversationPayload): Promise<HttpResponse<ConversationList>> {
		// CaseId and clientId give me the special AI conversations
		// conversationId give me the normal conversations
		const params = new URLSearchParams({
			...(caseId && { caseId }),
			...(clientId && { clientId }),
			...(conversationId && { conversationId }),
			...(page && { page: String(page) }),
			...(limit && { limit: String(limit) }),
		});
		return this.client.get(`/inbox/conversation/messages?${params}`);
	}
	async getConversationInfo(
		conversationId: string
	): Promise<HttpResponse<ConversationInfo>> {
		return this.client.get(`/inbox/conversation/${conversationId}`);
	}
	async postAddMessage(
		data: NewAddMessagePayload
	): Promise<HttpResponse<InboxCaseData>> {
		const params = {
			...(data.conversationId && { conversationId: data.conversationId }),
			...(data.applicationId && { applicationId: String(data.applicationId) }),
			...(data.caseId && { caseId: data.caseId }),
			...(data.clientId && { clientId: data.clientId }),
			...(data.content && { content: data.content }),
			...(data.is_bot && { is_bot: String(data.is_bot) }),
			...(data.action_required_id && {
				action_required_id: data.action_required_id,
			}),
			...(data.message_id && { message_id: data.message_id }),
			...(data.document_url && { document_url: data.document_url }),
			...(data.document_name && { document_name: data.document_name }),
		};
		return this.client.post('/inbox/conversation/add-message', params);
	}

	// Lawyer
	async getLawyerCases(): Promise<HttpResponse<LawyerCases>> {
		return this.client.get(`/inbox/lawyer-cases`);
	}

	async getLawyerClients(): Promise<HttpResponse<LawyerClients>> {
		return this.client.get(`/inbox/lawyer-clients`);
	}

	async getCasesByClient(id: string): Promise<HttpResponse<InboxCaseData>> {
		return this.client.get(`/inbox/lawyer-client-cases/${id}`);
	}

	async resolvedConversation(
		id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/inbox/conversation/mark-as-closed`, {
			conversationId: id,
		});
	}

	async markConversationUnread(
		id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/inbox/conversation/${id}/mark-as-unread`);
	}

	async markConversationRead(
		id: string
	): Promise<HttpResponse<{ message: string }>> {
		return this.client.post(`/inbox/conversation/${id}/mark-as-read`);
	}

	async postSendMessageToAll(
		payload: SendMessageToAllPayload
	): Promise<HttpResponse<{ message: string }>> {
		const payloadAny = payload as any;
		const flagged_reasons =
			payloadAny.flagged_reasons && payloadAny.flagged_reasons.length > 0
				? payloadAny.flagged_reasons
				: '';

		const flagged = flagged_reasons ? true : payload.flagged;

		const params = {
			...(payload.case_id && { case_id: payload.case_id }),
			...(payload.content && { content: payload.content }),
			...(payload.users_ids && { users_ids: payload.users_ids }),
			...(payload.search && { search: payload.search }),
			...(payload.orderBy && { orderBy: payload.orderBy }),
			...(payload.status && { status: payload.status }),
			...(payload.messageFilter && { messageFilter: payload.messageFilter }),
			...(payload.start_date && { start_date: payload.start_date }),
			...(payload.end_date && { end_date: payload.end_date }),
			...(flagged && { flagged }),
			...(payload.questions && { questions: payload.questions }),
			...(flagged_reasons && { flagged_reasons }),
			...(payload.stepperStatusIds &&
				payload.stepperStatusIds.length > 0 && {
					stepperStatusIds: payload.stepperStatusIds,
				}),
			...(payload.documents &&
				payload.documents.length > 0 && {
					documents: payload.documents,
				}),
		};

		return this.client.post('/inbox/send-message-to-all', params);
	}
}

export const inboxService = new InboxService(axiosClient);
